import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  DirectionsCar as VehicleIcon,
  ErrorOutline as ErrorIcon,
  HourglassEmpty as PendingIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  NotificationsActive as AlarmIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Router as ReceiverIcon,
  Sensors as SensorIcon,
  Speed as SchematicIcon,
  ConfirmationNumber as TicketIcon,
  Assignment as AssignmentIcon,
  EmojiEvents as CompleteIcon,
  Bolt as SimulateIcon,
  MyLocation as MyLocationIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTutorialContext } from '../../contexts/TutorialContext';

const STATUS_LABELS = {
  pending: i18n.t('hardcoded.components_admin_ordermanagement.pendiente'),
  running: i18n.t('hardcoded.components_auth_register.validando'),
  completed: i18n.t('hardcoded.components_demo_progresstracker.completado'),
  error: i18n.t('hardcoded.components_tutorial_tutorialtooltip.accion_requerida'),
};

const STATUS_COLORS = {
  pending: 'default',
  running: 'warning',
  completed: 'success',
  error: 'error',
};

const STEP_ICONS = {
  business: <BusinessIcon fontSize="small" />,
  vehicle: <VehicleIcon fontSize="small" />,
  receiver: <ReceiverIcon fontSize="small" />,
  sensor: <SensorIcon fontSize="small" />,
  assignment: <AssignmentIcon fontSize="small" />,
  alarm: <AlarmIcon fontSize="small" />,
  schematic: <SchematicIcon fontSize="small" />,
  simulate: <SimulateIcon fontSize="small" />,
  ticket: <TicketIcon fontSize="small" />,
  complete: <CompleteIcon fontSize="small" />,
};

const TutorialTooltip = ({ targetRect }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const {
    currentStep,
    currentStepIndex,
    totalSteps,
    stepStatus,
    isSimulating,
    simulationPhase,
    executeCurrentAction,
    skipTutorial,
    prevStep,
    nextStep,
  } = useTutorialContext();

  const progress = useMemo(() => {
    const statuses = Object.values(stepStatus || {});
    if (!statuses.length) return 0;
    const completed = statuses.filter((s) => s.status === 'completed').length;
    return Math.round((completed / statuses.length) * 100);
  }, [stepStatus]);

  const tooltipStyle = useMemo(() => {
    if (isMobile) {
      return {
        position: 'fixed',
        bottom: 12,
        left: 8,
        right: 8,
        maxHeight: '55vh',
        overflow: 'auto',
        zIndex: 10000,
      };
    }

    const width = 440;
    const margin = 20;

    if (!targetRect) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width,
        zIndex: 10000,
      };
    }

    const spaceRight = window.innerWidth - (targetRect.left + targetRect.width);
    const spaceLeft = targetRect.left;
    const spaceBelow = window.innerHeight - (targetRect.top + targetRect.height);

    let left, top;

    if (spaceRight >= width + margin) {
      left = targetRect.left + targetRect.width + margin;
      top = Math.max(12, Math.min(window.innerHeight - 600, targetRect.top));
    } else if (spaceLeft >= width + margin) {
      left = targetRect.left - width - margin;
      top = Math.max(12, Math.min(window.innerHeight - 600, targetRect.top));
    } else if (spaceBelow >= 300) {
      left = Math.max(12, Math.min(window.innerWidth - width - 12, targetRect.left));
      top = targetRect.top + targetRect.height + margin;
    } else {
      left = Math.max(12, Math.min(window.innerWidth - width - 12, targetRect.left));
      top = Math.max(12, targetRect.top - 400);
    }

    return {
      position: 'fixed',
      top,
      left,
      width,
      maxHeight: '80vh',
      overflow: 'auto',
      zIndex: 10000,
    };
  }, [isMobile, targetRect]);

  if (!currentStep) return null;

  const currentStatus = stepStatus?.[currentStep.id]?.status || 'pending';
  const currentNote = stepStatus?.[currentStep.id]?.note || '';
  const hasRoute = Boolean(currentStep.route);
  const isOnCorrectPage = hasRoute && location.pathname === currentStep.route;
  const isRunning = currentStatus === 'running' || isSimulating;
  const isCompleted = currentStatus === 'completed';
  const isSimStep = currentStep.actionType === 'simulate';
  const stepIcon = STEP_ICONS[currentStep.icon] || <PendingIcon fontSize="small" />;

  const handleGoToModule = () => {
    if (currentStep.route) {
      navigate(currentStep.route);
    }
  };

  const statusIcon = {
    pending: <PendingIcon fontSize="small" />,
    running: <RefreshIcon fontSize="small" sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />,
    completed: <CheckCircleIcon fontSize="small" />,
    error: <ErrorIcon fontSize="small" />,
  }[currentStatus];

  return (
    <Box sx={{ ...tooltipStyle, pointerEvents: 'auto' }}>
      <Paper
        elevation={24}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: '2px solid',
          borderColor: isCompleted
            ? theme.palette.success.main
            : currentStatus === 'error'
            ? theme.palette.warning.main
            : theme.palette.error.main,
          backgroundColor: theme.palette.background.paper,
          boxShadow: '0 12px 48px rgba(0,0,0,0.35)',
        }}
      >
        {/* ── Header ────────────────────────────────────────────────── */}
        <Box
          sx={{
            px: 2,
            py: 1.2,
            background: `linear-gradient(135deg, #1a1a1a 0%, #d32f2f 100%)`,
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {stepIcon}
            <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.5, fontSize: '0.72rem' }}>
              {i18n.t('demo.title')}</Typography>
          </Stack>
          <Chip
            size="small"
            label={t('hardcoded.components_tutorial_tutorialtooltip.paso_currentstepinde_de_totalsteps', { currentStepInde: currentStepIndex + 1, totalSteps: totalSteps })}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 800,
              fontSize: '0.72rem',
              height: 24,
            }}
          />
        </Box>

        {/* ── Progress bar ──────────────────────────────────────────── */}
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 6,
            backgroundColor: theme.palette.grey[200],
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #d32f2f, #ff5252)',
            },
          }}
        />

        <Box sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            {/* ── Step title ──────────────────────────────────────────── */}
            <Typography variant="h6" sx={{ fontWeight: 900, fontSize: '1.05rem', lineHeight: 1.3, color: theme.palette.text.primary }}>
              {currentStep.title}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem', lineHeight: 1.4 }}>
              {currentStep.description}
            </Typography>

            {/* ── Action instruction ───────────────────────────────────── */}
            <Box
              sx={{
                backgroundColor: '#fff3e0',
                border: '2px solid #ff9800',
                borderRadius: 2,
                p: 1.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#e65100', lineHeight: 1.5, mb: 0.5 }}
              >
                {currentStep.instruction}
              </Typography>
            </Box>

            {/* ── Detailed help items ──────────────────────────────────── */}
            {currentStep.detailedHelp && currentStep.detailedHelp.length > 0 && (
              <Box
                sx={{
                  backgroundColor: theme.palette.grey[50],
                  borderRadius: 2,
                  p: 1.5,
                  border: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                {currentStep.detailedHelp.map((item, idx) => (
                  item === '' ? (
                    <Box key={idx} sx={{ height: 8 }} />
                  ) : (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{
                        fontSize: '0.8rem',
                        lineHeight: 1.6,
                        color: theme.palette.text.primary,
                        fontWeight: item.includes(':') ? 600 : 400,
                        pl: 1,
                        borderLeft: item.startsWith('Sensor') || item.startsWith('Asigna') || item.startsWith('Dirección') || item.startsWith('Nombre') || item.startsWith('Marca') || item.startsWith(t('hardcoded.components_alarmprofiles_profilecard.presion')) || item.startsWith('Temperatura')
                          ? `3px solid ${theme.palette.error.main}`
                          : 'none',
                        ml: item.startsWith('Sensor') || item.startsWith('Asigna') || item.startsWith('Dirección') || item.startsWith('Nombre') || item.startsWith('Marca') || item.startsWith(t('hardcoded.components_alarmprofiles_profilecard.presion')) || item.startsWith('Temperatura')
                          ? 1
                          : 0,
                        mb: 0.3,
                      }}
                    >
                      {item}
                    </Typography>
                  )
                ))}
              </Box>
            )}

            {/* ── Simulation phase indicator ──────────────────────────── */}
            {isSimulating && simulationPhase && (
              <Box
                sx={{
                  backgroundColor: '#e3f2fd',
                  border: '1.5px solid #1976d2',
                  borderRadius: 2,
                  p: 1.2,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <RefreshIcon fontSize="small" sx={{ color: '#1976d2', animation: 'spin 1s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1565c0', fontSize: '0.82rem' }}>
                    {simulationPhase}
                  </Typography>
                </Stack>
              </Box>
            )}

            {/* ── Status & note ────────────────────────────────────────── */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip
                size="small"
                color={STATUS_COLORS[currentStatus] || 'default'}
                label={STATUS_LABELS[currentStatus] || t('hardcoded.components_admin_ordermanagement.pendiente')}
                icon={statusIcon}
                sx={{ fontWeight: 700, fontSize: '0.72rem' }}
              />
              {currentNote && (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    color: currentStatus === 'error'
                      ? theme.palette.warning.dark
                      : currentStatus === 'completed'
                      ? theme.palette.success.dark
                      : theme.palette.text.secondary,
                    flex: 1,
                    minWidth: 0,
                    wordBreak: 'break-word',
                  }}
                >
                  {currentNote}
                </Typography>
              )}
            </Stack>

            <Divider />

            {/* ── Navigation button ────────────────────────────────────── */}
            {hasRoute && !isOnCorrectPage && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<MyLocationIcon />}
                onClick={handleGoToModule}
                fullWidth
                size="small"
                sx={{ fontWeight: 700, fontSize: '0.8rem', py: 0.8 }}
              >
                {t('hardcoded.components_tutorial_tutorialtooltip.ir_al_modulo_correcto')}</Button>
            )}

            {hasRoute && isOnCorrectPage && (
              <Chip
                label={t('hardcoded.components_tutorial_tutorialtooltip.estas_en_el_modulo_correcto')}
                color="success"
                icon={<CheckCircleIcon />}
                sx={{ fontWeight: 700, fontSize: '0.75rem', justifyContent: 'flex-start' }}
              />
            )}

            {/* ── Main action button ───────────────────────────────────── */}
            <Button
              variant="contained"
              color={isCompleted ? 'success' : isSimStep ? 'error' : 'primary'}
              startIcon={
                isCompleted ? <NextIcon />
                : isSimStep ? <PlayArrowIcon />
                : isRunning ? <RefreshIcon sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
                : <RefreshIcon />
              }
              onClick={isCompleted && currentStepIndex < totalSteps - 1 ? nextStep : executeCurrentAction}
              disabled={isRunning}
              fullWidth
              size="large"
              sx={{
                fontWeight: 800,
                fontSize: '0.92rem',
                py: 1.2,
                boxShadow: 4,
                '&:hover': { boxShadow: 6 },
              }}
            >
              {isRunning
                ? (isSimulating ? t('hardcoded.components_tutorial_tutorialtooltip.simulando') : t('hardcoded.components_auth_register.validando'))
                : isCompleted && currentStepIndex < totalSteps - 1
                ? t('hardcoded.components_tutorial_tutorialtooltip.siguiente_paso')
                : currentStep.actionLabel || t('hardcoded.components_tutorial_tutorialtooltip.validar_paso')
              }
            </Button>

            {/* ── Bottom bar: prev + close ──────────────────────────────── */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              {currentStepIndex > 0 ? (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<PrevIcon />}
                  onClick={prevStep}
                  sx={{ fontSize: '0.74rem', color: 'text.secondary' }}
                >
                  {i18n.t('hardcoded.components_tutorial_tutorialtooltip.anterior')}</Button>
              ) : (
                <Box />
              )}
              <Button
                variant="text"
                color="error"
                size="small"
                startIcon={<CloseIcon />}
                onClick={skipTutorial}
                sx={{ fontSize: '0.74rem' }}
              >
                {t('hardcoded.components_tutorial_tutorialtooltip.cerrar_guia')}</Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default TutorialTooltip;
